import { useMutation, useQueryClient } from '@tanstack/react-query';
import { plantApi } from '../lib/plantApi';
import { PlantVariety } from '../types/plant';

export function useCreatePlantVariety() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (varietyData: {
      speciesId: string;
      primaryCommonName: string;
      subspecies?: string | null;
      cultivarName?: string | null;
      alternativeNames?: string[];
      plantType?: string | null;
      category?: string | null;
    }) => plantApi.createPlantVariety(varietyData),
    onSuccess: (newVariety: PlantVariety) => {
      // Invalidate variety search queries to include the new variety
      queryClient.invalidateQueries({ queryKey: ['plantVarieties'] });

      // Optionally, we can add the new variety to the cache immediately
      queryClient.setQueryData(['plantVariety', newVariety.id], newVariety);
    },
  });
}

export function useCreateGenus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => plantApi.createGenus(name),
    onSuccess: () => {
      // Invalidate genus queries
      queryClient.invalidateQueries({ queryKey: ['genera'] });
    },
  });
}

export function useCreateSpecies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (speciesData: { name: string; genusId: string }) =>
      plantApi.createSpecies(speciesData),
    onSuccess: (newSpecies, variables) => {
      // Invalidate species queries for the specific genus
      queryClient.invalidateQueries({ queryKey: ['species', variables.genusId] });
    },
  });
}

export function useCreateCompleteVariety() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (varietyData: {
      genusName: string;
      speciesName: string;
      primaryCommonName: string;
      subspecies?: string | null;
      cultivarName?: string | null;
      alternativeNames?: string[];
      plantType?: string | null;
      tags?: string[];
    }) => plantApi.createCompleteVariety(varietyData),
    onSuccess: (newVariety: PlantVariety) => {
      // Invalidate variety search queries to include the new variety
      queryClient.invalidateQueries({ queryKey: ['plantVarieties'] });

      // Invalidate genus and species queries as new ones might have been created
      queryClient.invalidateQueries({ queryKey: ['genera'] });
      queryClient.invalidateQueries({ queryKey: ['species'] });

      // Optionally, add the new variety to the cache immediately
      queryClient.setQueryData(['plantVariety', newVariety.id], newVariety);
    },
  });
}

export function useUpdatePlantVariety() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      id: string;
      varietyData: {
        primaryCommonName: string;
        subspecies?: string | null;
        cultivarName?: string | null;
        alternativeNames?: string[];
        plantType?: string | null;
        growthPattern?: string | null;
        tags?: string[];
      };
    }) => plantApi.updatePlantVariety(data.id, data.varietyData),
    onSuccess: (updatedVariety: PlantVariety) => {
      // Invalidate variety search queries
      queryClient.invalidateQueries({ queryKey: ['plantVarieties'] });

      // Update the specific variety in cache
      queryClient.setQueryData(['plantVariety', updatedVariety.id], updatedVariety);
    },
  });
}
